import dayjs from "dayjs";

export const SEM_CATEGORIA = "sem_categoria";
export const SEM_CATEGORIA_LABEL = "Sem Categoria";
export const SEM_ZONA_VINCULADA = "Sem zona vinculada";

const numero = (valor) => Number(valor) || 0;
const CENTAVO = 0.009;

export const chaveDaCategoria = (item) => {
  if (!item) return null;
  if (item.category_source === "ouran") {
    return item.category_id == null ? null : `ouran:${item.category_id}`;
  }
  return item.collection_legacy_id == null
    ? null
    : `erp:${item.collection_legacy_id}`;
};

export const chaveDoEstoque = (item) =>
  item?.category_source === "ouran"
    ? chaveDaCategoria({
        category_source: "ouran",
        category_id: item.categoria_id,
      })
    : chaveDaCategoria({ collection_legacy_id: item?.legacy_id });

export const identidadeDaChave = (chave) => {
  const [fonte, id] = String(chave).split(":");
  return {
    legacy_id: Number(id),
    category_source: fonte === "ouran" ? "ouran" : undefined,
  };
};

export const filtroDaLinha = (linha) => {
  if (!linha || linha.legacy_id == null) return {};
  return linha.category_source === "ouran"
    ? { category_source: "ouran", category_id: [linha.legacy_id] }
    : { collection_legacy_id: [linha.legacy_id] };
};

export const chavesSelecionadas = (filtros) => {
  if (filtros?.category_id?.length) {
    return new Set(filtros.category_id.map((id) => `ouran:${id}`));
  }
  if (filtros?.collection_legacy_id?.length) {
    return new Set(filtros.collection_legacy_id.map((id) => `erp:${id}`));
  }
  return null;
};

export const filtrosDaReceita = (filtros) =>
  filtros?.seller?.length ? { seller: filtros.seller } : {};

export const filtrosDoRateio = (filtros) =>
  filtros?.zone_id?.length ? { zone_id: filtros.zone_id } : {};

const nomesDasZonas = (rateio) =>
  new Map((rateio?.zones || []).map((zona) => [zona.zone_id, zona.zone_name]));

const porChave = (itens, chaveDe) => {
  const mapa = new Map();
  (itens || []).forEach((item) => {
    const chave = chaveDe(item);
    if (chave) mapa.set(chave, item);
  });
  return mapa;
};

const dataIso = (valor) => {
  if (!valor) return null;
  const data = dayjs(valor);
  return data.isValid() ? data.format("YYYY-MM-DD") : null;
};

export const linhaSemCategoria = (grupo, modoOuran) => {
  const total = Math.round(numero(grupo?.bruto_vendas) * 100) / 100;
  if (total <= CENTAVO) return null;
  return {
    key: SEM_CATEGORIA,
    legacy_id: null,
    sem_categoria: true,
    label: SEM_CATEGORIA_LABEL,
    total_revenue: total,
    revenue: 0,
    revenue_outside_linked_period: total,
    permanence: 0,
    days: 0,
    revenue_per_day: 0,
    zones: [],
    zone_ids: [],
    units_sold: Math.trunc(numero(grupo?.unidades)),
    available_stock: null,
    stock_ref_date: null,
    ...(modoOuran ? { category_source: "ouran" } : {}),
  };
};

export const linhasDaCategoria = ({
  receita,
  rateio,
  atencao,
  estoque,
  filtros,
  modoOuran,
}) => {
  const comZona = Boolean(filtros?.zone_id?.length);
  const comVendedor = Boolean(filtros?.seller?.length);
  const selecionadas = chavesSelecionadas(filtros);
  const grupos = porChave(receita?.groups, (grupo) => grupo.key);
  const colecoes = porChave(rateio?.collections, chaveDaCategoria);
  const exposicao = porChave(atencao?.branches?.[0]?.groups, chaveDaCategoria);
  const loja = estoque?.branches?.[0];
  const estoques = porChave(loja?.colecoes, chaveDoEstoque);
  const refDoEstoque = dataIso(loja?.ref_date);
  const zonas = nomesDasZonas(rateio);

  const chaves = new Set([
    ...grupos.keys(),
    ...colecoes.keys(),
    ...exposicao.keys(),
  ]);
  chaves.delete(SEM_CATEGORIA);

  const linhas = [];
  chaves.forEach((chave) => {
    if (selecionadas && !selecionadas.has(chave)) return;
    const grupo = grupos.get(chave);
    const colecao = colecoes.get(chave);
    const atencaoDaCategoria = exposicao.get(chave);
    const dias = numero(colecao?.dias_em_exposicao_selecionadas);
    const zoneIds = colecao?.zonas || [];
    if (comZona && dias <= 0 && !zoneIds.length && !atencaoDaCategoria) return;

    const totalRevenue = numero(grupo?.bruto_vendas);
    const revenue = dias > 0 ? numero(colecao?.faturamento_rateado) : 0;
    const permanence = numero(atencaoDaCategoria?.pessoa_minuto);
    if (totalRevenue <= 0 && permanence <= 0 && dias <= 0) return;
    if (comVendedor && totalRevenue <= 0) return;

    const vinculado = numero(colecao?.faturamento_vinculado);
    const foraDaVigencia = Math.max(totalRevenue - vinculado, 0);
    let nomesDeZona = zoneIds.map((id) => zonas.get(id) ?? String(id));
    if (!nomesDeZona.length && totalRevenue > 0 && !comZona) {
      nomesDeZona = [SEM_ZONA_VINCULADA];
    }
    const estoqueDaCategoria = estoques.get(chave);
    const { legacy_id, category_source } = identidadeDaChave(chave);

    linhas.push({
      key: chave,
      legacy_id,
      category_source,
      label:
        grupo?.category_name ||
        colecao?.colecao ||
        atencaoDaCategoria?.category_name ||
        `Categoria ${legacy_id}`,
      total_revenue: Math.round(totalRevenue * 100) / 100,
      revenue: Math.round(revenue * 100) / 100,
      revenue_outside_linked_period: Math.round(foraDaVigencia * 100) / 100,
      permanence,
      days: dias,
      revenue_per_day: dias > 0 ? Math.round((revenue / dias) * 100) / 100 : 0,
      zones: nomesDeZona,
      zone_ids: zoneIds,
      units_sold: Math.trunc(numero(grupo?.unidades)),
      available_stock: refDoEstoque
        ? numero(estoqueDaCategoria?.disponivel)
        : null,
      stock_ref_date: refDoEstoque,
    });
  });

  linhas.sort((a, b) => b.total_revenue - a.total_revenue);
  if (!comZona && !selecionadas) {
    const semCategoria = linhaSemCategoria(
      grupos.get(SEM_CATEGORIA),
      modoOuran
    );
    if (semCategoria) linhas.push(semCategoria);
  }
  return linhas;
};

export const precisaAtencaoDaZona = (linha, filtros) =>
  Boolean(filtros?.zone_id?.length) && linha?.legacy_id != null;

export const comAtencaoDaZona = (linhas, atencaoPorChave) =>
  (linhas || []).map((linha) => {
    if (linha.legacy_id == null) return linha;
    return { ...linha, permanence: numero(atencaoPorChave?.[linha.key]) };
  });

export const zonasDaReceitaDoVendedor = (linha, filtros) =>
  filtros?.zone_id?.length ? filtros.zone_id : linha?.zone_ids || [];

export const precisaReceitaDoVendedor = (linha, filtros) =>
  Boolean(filtros?.seller?.length) &&
  linha?.legacy_id != null &&
  linha.total_revenue > 0 &&
  zonasDaReceitaDoVendedor(linha, filtros).length > 0;

export const comReceitaDoVendedor = (linhas, receitaPorChave, filtros) => {
  const comZona = Boolean(filtros?.zone_id?.length);
  return (linhas || []).map((linha) => {
    if (!precisaReceitaDoVendedor(linha, filtros)) {
      return linha.legacy_id == null || !filtros?.seller?.length
        ? linha
        : { ...linha, revenue: 0, revenue_per_day: 0 };
    }
    const revenue =
      Math.round(numero(receitaPorChave?.[linha.key]) * 100) / 100;
    const emZona = linha.days > 0 ? revenue : 0;
    return {
      ...linha,
      revenue: emZona,
      revenue_per_day:
        linha.days > 0 ? Math.round((emZona / linha.days) * 100) / 100 : 0,
      revenue_outside_linked_period: comZona
        ? 0
        : Math.round(Math.max(linha.total_revenue - revenue, 0) * 100) / 100,
    };
  });
};

const diasDoPeriodo = (start, end) => {
  const dias = [];
  let dia = dayjs(start);
  const fim = dayjs(end);
  while (dia.isValid() && fim.isValid() && dia.isBefore(fim)) {
    dias.push(dia.format("YYYY-MM-DD"));
    dia = dia.add(1, "day");
  }
  return dias;
};

const exposicaoPorDia = (atencao) =>
  new Map(
    (atencao?.branches?.[0]?.groups || []).map((grupo) => [
      grupo.key,
      numero(grupo.pessoa_minuto),
    ])
  );

export const serieDaCategoria = ({
  start,
  end,
  rateio,
  atencao,
  eventos,
  zoneIds,
}) => {
  const receitaPorDia = new Map();
  (rateio?.series || []).forEach((dia) => {
    receitaPorDia.set(
      dia.date,
      (receitaPorDia.get(dia.date) || 0) + numero(dia.faturamento_rateado)
    );
  });
  const permanenciaPorDia = exposicaoPorDia(atencao);
  const zonasDaCategoria = new Set(zoneIds || []);
  return {
    timeseries: diasDoPeriodo(start, end).map((date) => ({
      date,
      revenue: receitaPorDia.get(date) || 0,
      permanence: permanenciaPorDia.get(date) || 0,
    })),
    events: (eventos?.events || [])
      .filter((evento) => zonasDaCategoria.has(evento.zona_id))
      .map((evento) => ({
        date: evento.data,
        title: evento.titulo || "",
        comment: evento.comentario || "",
        zone: evento.zona_nome || "",
        end_date: evento.event_end_date,
      })),
  };
};

export const paramsDoDetalhe = ({ branchId, start, end, filtros, linha }) => {
  const recorte = linha?.sem_categoria
    ? { sem_categoria: true }
    : linha?.category_source === "ouran"
      ? { category_source: "ouran", category_id: linha.legacy_id }
      : { collection_legacy_id: linha?.legacy_id };
  return {
    branch_id: branchId,
    start,
    end,
    ...filtrosDoRateio(filtros),
    ...filtrosDaReceita(filtros),
    ...recorte,
  };
};
